import { intl } from '@fullstory/intl';
import { TrendFrequency } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { getAdjustedEndDate } from '@fullstory/util/date';
import { Point } from '@fullstory/visualizations';
import { ScaleTime, timeSunday, timeDay, timeMonday, timeMonth } from 'd3';

type PointDate = Pick<Point, 'startDate' | 'endDate'>;

export const getPointDate = (
  point: PointDate,
  isEndDateAligned: boolean,
  frequency: TrendFrequency,
) =>
  isEndDateAligned
    ? frequency === TrendFrequency.TREND_FREQUENCY_HOURLY ||
      frequency === TrendFrequency.TREND_FREQUENCY_MINUTELY
      ? point.endDate
      : getAdjustedEndDate(point.endDate).toDate()
    : point.startDate;

const DAY_IN_MILLIS = 1000 * 60 * 60 * 24;
const HOUR_TO_DAY_LIMIT = 3;

export const getXTicks = (
  xScale: ScaleTime<number, number>,
  frequency: TrendFrequency,
  maxLength: number,
  isEndDateAligned: boolean,
  useHourlyToDayLabels: boolean,
) => {
  const domain = xScale.domain();
  const width = (domain[1]?.getTime() ?? 0) - (domain[0]?.getTime() ?? 0);
  const durationInDays = width / DAY_IN_MILLIS;

  // prevent more ticks than data points
  let xTicks = xScale.ticks();
  if (maxLength <= xTicks.length) {
    if (
      frequency === TrendFrequency.TREND_FREQUENCY_HOURLY ||
      frequency === TrendFrequency.TREND_FREQUENCY_MINUTELY
    ) {
      xTicks = xScale.ticks(maxLength);
    } else if (frequency === TrendFrequency.TREND_FREQUENCY_DAILY) {
      xTicks = xScale.ticks(timeDay);
    } else if (frequency === TrendFrequency.TREND_FREQUENCY_WEEKLY) {
      xTicks = xScale.ticks(isEndDateAligned ? timeSunday : timeMonday);
    } else if (frequency === TrendFrequency.TREND_FREQUENCY_MONTHLY) {
      xTicks = xScale.ticks(timeMonth);

      if (isEndDateAligned) {
        if (xTicks.length >= 1) {
          const lastMonth = xTicks[xTicks.length - 1]!;
          xTicks.push(timeMonth.offset(lastMonth, 1));
        }
        for (let i = 0; i < xTicks.length; i++) {
          const old = xTicks[i]!;
          xTicks[i] = timeDay.offset(old, -1);
        }
      }
    }
  } else if (frequency === TrendFrequency.TREND_FREQUENCY_WEEKLY) {
    // We want to always align weekly to Mondays but we don't want too many ticks/lines, so
    // we align the ticks to Mondays and set the tick interval to be the every other week
    xTicks = xScale.ticks(
      isEndDateAligned ? timeSunday.every(2) ?? timeSunday : timeMonday.every(2) ?? timeMonday,
    );
  } else if (
    useHourlyToDayLabels &&
    frequency === TrendFrequency.TREND_FREQUENCY_HOURLY &&
    durationInDays > HOUR_TO_DAY_LIMIT
  ) {
    xTicks = xScale.ticks(timeDay);
  }

  return xTicks;
};

export const getDurationInDays = (xScale: ScaleTime<number, number>) => {
  const domain = xScale.domain();
  const width = (domain[1]?.getTime() ?? 0) - (domain[0]?.getTime() ?? 0);
  return width / DAY_IN_MILLIS;
};

export function xTimeFormat(
  date: Date,
  frequency: TrendFrequency,
  durationInDays: number,
  useHourlyToDayLabels: boolean,
): string {
  if (
    frequency === TrendFrequency.TREND_FREQUENCY_HOURLY &&
    durationInDays > HOUR_TO_DAY_LIMIT &&
    useHourlyToDayLabels
  ) {
    return intl.formatDate(date, { day: '2-digit', month: 'numeric' });
  } else if (
    frequency === TrendFrequency.TREND_FREQUENCY_HOURLY ||
    frequency === TrendFrequency.TREND_FREQUENCY_MINUTELY
  ) {
    return intl.formatTime(date, { timeStyle: 'short' });
  } else if (
    frequency === TrendFrequency.TREND_FREQUENCY_WEEKLY ||
    frequency === TrendFrequency.TREND_FREQUENCY_MONTHLY
  ) {
    return intl.formatDate(date, { month: 'numeric', day: '2-digit', year: '2-digit' });
  }
  return intl.formatDate(date, { day: '2-digit', month: 'numeric' });
}
